/**
 * Admin → Portfolios → Setup upload (renamed from "Setup", Pedro P1 2026-10-21): each bank's positions-file LAYOUT (Banks arc step 3b, migration
 * 0114; design §2.6, Pedro F7 / G1 / G2: "build the editor now, this bank pre-filled").
 *
 * A layout says how one bank's file is read: which heading fills each of the app's fields
 * (by heading NAME), which categories mean cash and loan, the portfolio-number rule and the
 * short key (characters a to b of the number), and the reference currency (a column, or a
 * fixed currency). A bank's files can be uploaded only once it has a layout. BNP's was set up
 * from the reader the app has always used; a new bank starts from it.
 *
 * Edit → Save changes / Discard (§3g Pattern A, leave-page guard), like Admin → Banks. Changes
 * apply to later uploads only; each upload keeps the layout it was read with. "Check a file's
 * heading row" reads row 1 of a file against the layout on screen (saved or not) and writes
 * nothing, so it works in either mode.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Grid, Group, List, NumberInput, Paper, Stack, Table, TagsInput, Text, TextInput,
  Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp, IconCheck, IconFileSearch, IconPencil, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  portfolioLayoutsApi, type HeaderCheck, type LayoutInput,
} from '../api/portfolioLayouts';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useReportTabDirty } from '../components/TabDirty';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Something went wrong.');
const same = (a: LayoutInput | null, b: LayoutInput | null) => JSON.stringify(a) === JSON.stringify(b);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

export function SetupTab() {
  const qc = useQueryClient();
  const banksQ = useQuery({ queryKey: ['portfolio-layouts'], queryFn: () => portfolioLayoutsApi.banks() });
  const fieldsQ = useQuery({ queryKey: ['portfolio-layouts', 'fields'], queryFn: () => portfolioLayoutsApi.fields(),
    staleTime: Infinity });
  const [selected, setSelected] = useState<string | null>(null);
  const bank = (banksQ.data ?? []).find((b) => b.bank_id === selected) ?? null;
  const layoutQ = useQuery({
    queryKey: ['portfolio-layouts', selected], queryFn: () => portfolioLayoutsApi.get(selected as string),
    enabled: !!bank?.has_layout,
  });

  const saved: LayoutInput | null = useMemo(() => {
    const l = layoutQ.data;
    return bank?.has_layout && l ? {
      columns: { ...l.columns }, cash_categories: [...l.cash_categories], loan_categories: [...l.loan_categories],
      number_min_digits: l.number_min_digits, number_max_digits: l.number_max_digits, pad_to: l.pad_to,
      key_from: l.key_from, key_to: l.key_to, ref_currency_fixed: l.ref_currency_fixed,
    } : null;
  }, [layoutQ.data, bank?.has_layout]);
  // A bank without a layout starts from BNP's (Pedro: "this bank pre-filled").
  const base = saved ?? fieldsQ.data?.template ?? null;

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<LayoutInput | null>(null);
  useEffect(() => { if (!editMode) setDraft(base); }, [base, editMode]);
  const dirty = editMode && (!saved || !same(draft, base));

  const save = useMutation({
    mutationFn: (d: LayoutInput) => portfolioLayoutsApi.save(selected as string, d),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['portfolio-layouts'] });
      setEditMode(false);
      notifications.show({ color: 'green', message: `Saved ${l.bank_mnemonic}'s file layout. It reads the next uploads.` });
    },
  });
  const onSave = async (): Promise<boolean> => {
    if (!draft) return false;
    try { await save.mutateAsync(draft); return true; } catch { return false; }
  };
  const confirmDiscard = useDirtyFormGuard(dirty, onSave);
  useReportTabDirty(dirty);
  const pick = (id: string) => confirmDiscard(() => { setSelected(id); setEditMode(false); save.reset(); setCheck(null); });

  const [check, setCheck] = useState<HeaderCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async (f: File) => {
    if (!draft) return;
    setChecking(true); setCheck(null);
    try { setCheck(await portfolioLayoutsApi.check(f, draft)); }
    catch (e) { notifications.show({ color: 'red', title: 'Not checked', message: errMsg(e) }); }
    finally { setChecking(false); }
  };

  const set = <K extends keyof LayoutInput>(k: K, v: LayoutInput[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const setHeading = (field: string, v: string) =>
    setDraft((d) => (d ? { ...d, columns: { ...d.columns, [field]: v } } : d));
  const fields = fieldsQ.data?.fields ?? [];
  const banks = banksQ.data ?? [];
  // A cleared box sends 0, which the server refuses in plain words ("must be between 1 and 20").
  const num = (v: string | number) => (typeof v === 'number' ? v : Number(v) || 0);

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Each bank&apos;s positions file is read through its layout. A bank&apos;s files can be uploaded only once it
            has one. Banks are added in Admin → Banks.
          </Text>
          {banksQ.error && <Alert color="red">{errMsg(banksQ.error)}</Alert>}
          <Table highlightOnHover withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr><Table.Th>Mnemonic</Table.Th><Table.Th>Name</Table.Th><Table.Th>File layout</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {banks.map((b) => (
                <Table.Tr key={b.bank_id} onClick={() => pick(b.bank_id)} style={{ cursor: 'pointer' }}
                  bg={selected === b.bank_id ? 'var(--mantine-color-azul-0)' : undefined}>
                  <Table.Td><Text ff="monospace" fw={600} size="sm">{b.mnemonic}</Text></Table.Td>
                  <Table.Td>{b.name}</Table.Td>
                  <Table.Td>
                    {b.has_layout ? (
                      <Tooltip withArrow label={`Last changed ${when(b.updated_at)}${b.updated_by ? ` by ${b.updated_by}` : ''}`}>
                        <Badge color="green" variant="light">Set</Badge>
                      </Tooltip>
                    ) : <Badge color="gray" variant="light">None</Badge>}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 8 }}>
        {!bank ? (
          <Text c="dimmed" size="sm">Choose a bank to see or set up its file layout.</Text>
        ) : !draft ? null : (
          <Paper withBorder p="md">
            <Stack gap="sm">
              <Group justify="space-between">
                <div>
                  <Title order={4}>{bank.mnemonic} · {bank.name} — file layout</Title>
                  <Text size="xs" c="dimmed">
                    {bank.has_layout
                      ? `Last changed ${when(bank.updated_at)}${bank.updated_by ? ` by ${bank.updated_by}` : ''}. Changes apply to later uploads only.`
                      : "No layout yet. Edit starts from BNP's layout: change what differs in this bank's file, then save."}
                  </Text>
                </div>
                {!editMode ? (
                  <Button leftSection={<IconPencil size={16} />} onClick={() => setEditMode(true)}>
                    {bank.has_layout ? 'Edit' : 'Set up'}
                  </Button>
                ) : (
                  <Group gap="xs">
                    <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
                      onClick={() => confirmDiscard(() => { setEditMode(false); setDraft(base); save.reset(); })}>
                      Discard
                    </Button>
                    <Button color="azul" loading={save.isPending} disabled={!dirty} onClick={() => void onSave()}>
                      Save changes
                    </Button>
                  </Group>
                )}
              </Group>
              {save.error && <Alert color="red" title="Not saved">{errMsg(save.error)}</Alert>}

              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                <Stack gap="md">
                  <div>
                    <Text fw={600} size="sm">Headings</Text>
                    <Text size="xs" c="dimmed" mb={4}>
                      The heading in row 1 of the bank&apos;s file for each of the app&apos;s fields, exactly as written.
                      Leave a field blank if the file has no such column (required fields must be filled). A heading
                      that is missing or appears twice in a file stops that upload.
                    </Text>
                    <Table withTableBorder fz="sm" verticalSpacing={2}>
                      <Table.Thead>
                        <Table.Tr><Table.Th>App field</Table.Th><Table.Th w={110}>Required</Table.Th><Table.Th>Heading in the file</Table.Th></Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {fields.map((f) => (
                          <Table.Tr key={f.key}>
                            <Table.Td>{f.label}</Table.Td>
                            <Table.Td>
                              {f.required ? <Badge size="xs" variant="light">Required</Badge>
                                : f.key === 'ref_ccy' ? <Text size="xs" c="dimmed">or fixed, below</Text> : null}
                            </Table.Td>
                            <Table.Td>
                              <TextInput size="xs" value={draft.columns[f.key] ?? ''}
                                onChange={(e) => setHeading(f.key, e.currentTarget.value)} />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>

                  <Group grow align="flex-start">
                    <TagsInput label="Categories that mean cash" size="xs" value={draft.cash_categories}
                      onChange={(v) => set('cash_categories', v)}
                      description="As in the file's category column (case is ignored). Press Enter after each." />
                    <TagsInput label="Categories that mean a loan" size="xs" value={draft.loan_categories}
                      onChange={(v) => set('loan_categories', v)}
                      description="A loan is an account, not a position, and is left out of the gross value." />
                  </Group>

                  <div>
                    <Text fw={600} size="sm">Portfolio number and short key</Text>
                    <Text size="xs" c="dimmed" mb={4}>
                      Digits only. A number shorter than &ldquo;Pad to&rdquo; gets zeros on the left (Excel drops them);
                      blank keeps it as given. The short key, used to find a portfolio, is the characters from &ldquo;Key
                      from&rdquo; to &ldquo;Key to&rdquo; of the number (BNP: 4 to 9, the 6 rightmost).
                    </Text>
                    <Group grow>
                      <NumberInput size="xs" label="Shortest (digits)" min={1} max={20} value={draft.number_min_digits}
                        onChange={(v) => set('number_min_digits', num(v))} />
                      <NumberInput size="xs" label="Longest (digits)" min={1} max={20} value={draft.number_max_digits}
                        onChange={(v) => set('number_max_digits', num(v))} />
                      <NumberInput size="xs" label="Pad to" min={1} max={20} value={draft.pad_to ?? ''}
                        onChange={(v) => set('pad_to', v === '' ? null : num(v))} />
                      <NumberInput size="xs" label="Key from" min={1} max={20} value={draft.key_from}
                        onChange={(v) => set('key_from', num(v))} />
                      <NumberInput size="xs" label="Key to" min={1} max={20} value={draft.key_to}
                        onChange={(v) => set('key_to', num(v))} />
                    </Group>
                  </div>

                  <TextInput size="xs" w={320} label="Fixed reference currency"
                    description="Only for a file with no reference-currency column (then leave that heading blank above)."
                    value={draft.ref_currency_fixed ?? ''} maxLength={3}
                    onChange={(e) => set('ref_currency_fixed', e.currentTarget.value.toUpperCase() || null)} />
                </Stack>
              </fieldset>

              <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
                <Stack gap="xs">
                  <Group gap="xs"><IconFileSearch size={16} /><Text fw={600} size="sm">Check a file&apos;s heading row</Text></Group>
                  <Text size="xs" c="dimmed">
                    Reads row 1 of a file from this bank against the layout on screen (saved or not). Nothing is saved
                    or uploaded.
                  </Text>
                  <XlsxDropzone hint="One Excel file: a positions file from this bank (only its heading row is read)."
                    onSelect={(f) => void runCheck(f)} loading={checking}
                    onReject={() => notifications.show({ color: 'red', title: 'Not accepted', message: 'Please choose a single .xlsx file.' })} />
                  {check && <CheckResult c={check} />}
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        )}
      </Grid.Col>
    </Grid>
  );
}

function CheckResult({ c }: { c: HeaderCheck }) {
  return (
    <Stack gap={6}>
      <Alert color={c.ok ? 'green' : 'red'} icon={c.ok ? <IconCheck size={16} /> : <IconX size={16} />}
        title={c.ok ? `${c.file_name}: every heading of the layout is there` : `${c.file_name}: this file would be stopped`}>
        <Text size="sm">{c.found.length} heading{c.found.length === 1 ? '' : 's'} found once, as the layout needs.</Text>
      </Alert>
      {c.missing.length > 0 && (
        <Text size="sm"><b>Missing</b> (not in row 1): {c.missing.map((m) => `"${m.heading}" (${m.label})`).join(', ')}</Text>
      )}
      {c.repeated.length > 0 && (
        <Text size="sm"><b>More than once:</b> {c.repeated.map((m) => `"${m.heading}" (${m.label})`).join(', ')}</Text>
      )}
      {c.unused.length > 0 && (
        <Text size="sm" c="dimmed">Headings no field uses (ignored): {c.unused.map((u) => `"${u}"`).join(', ')}</Text>
      )}
      {c.problems.length > 0 && (
        <Alert color="yellow" title="The layout itself cannot be saved yet">
          <List size="sm">{c.problems.map((p) => <List.Item key={p}>{p}</List.Item>)}</List>
        </Alert>
      )}
    </Stack>
  );
}
